import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateUserPreferences } from '../../redux/slices/pdfSlice';
import UserTopbar from '../UserTopbar';
import { toast } from "react-toastify";
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { CURRENCY_SYMBOLS, getCurrencySymbol } from '../../Helpers/currencyHelper';
import Apiurl from '../../Helpers/apiUrl';

const Settings = () => {
    const dispatch = useDispatch();
    const {
        showSmartSuggestions,
        showStockWarning,
        showLowStockWarning,
        currency // Pulled from Redux state
    } = useSelector((state) => state.pdfImages || {});

    const [localSmartSuggestions, setLocalSmartSuggestions] = useState(showSmartSuggestions ?? true);
    const [localStockWarning, setLocalStockWarning] = useState(showStockWarning ?? true);
    const [localLowStockWarning, setLocalLowStockWarning] = useState(showLowStockWarning ?? true);
    
    const [localCurrency, setLocalCurrency] = useState(currency || 'INR');
    const [rates, setRates] = useState({});


useEffect(() => {
    const token = localStorage.getItem('u_token');

    fetch(`${Apiurl}admin/currency/list`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    })
        .then((res) => {
            if (!res.ok) {
                throw new Error(`API Error: ${res.status}`);
            }
            return res.json();
        })
        .then((data) => {
            if (data?.status && Array.isArray(data.data)) {

                // Convert API currency array into rates object
                const rates = data.data
                    .filter(currency => currency.is_active)
                    .reduce((acc, currency) => {
                        acc[currency.code] = Number(currency.exchange_rate);
                        return acc;
                    }, {});

                const formattedRates = {
                    INR: 1.0,
                    ...rates
                };

                setRates(formattedRates);

                localStorage.setItem(
                    'cached_exchange_rates',
                    JSON.stringify(formattedRates)
                );
            }
        })
        .catch((err) => {
            console.error(
                "Failed to fetch live currency rates from API:",
                err
            );
        });
}, []);


    useEffect(() => {
        setLocalSmartSuggestions(showSmartSuggestions ?? true);
        setLocalStockWarning(showStockWarning ?? true);
        setLocalLowStockWarning(showLowStockWarning ?? true);
        if (currency) {
            setLocalCurrency(currency);
        }
    }, [showSmartSuggestions, showStockWarning, showLowStockWarning, currency]);

    const handleUpdatePreferences = (updatedFields, successMessage) => {
        const payload = {
            show_smart_suggestions: localSmartSuggestions,
            show_stock_warning: localStockWarning,
            show_low_stock_warning: localLowStockWarning,
            currency: localCurrency,
            currency_symbol: getCurrencySymbol(localCurrency),
            ...updatedFields
        };

        dispatch(updateUserPreferences(payload))
            .unwrap()
            .then(() => {
                if (successMessage) {
                    toast.success(successMessage);
                }
            })
            .catch((err) => {
                console.error("Error updating preferences:", err);
                toast.error("Failed to update preferences");
            });
    };

    const handleToggle = (key, value) => {
        if (key === 'show_smart_suggestions') setLocalSmartSuggestions(value);
        if (key === 'show_stock_warning') setLocalStockWarning(value);
        if (key === 'show_low_stock_warning') setLocalLowStockWarning(value);

        const readableName = key === 'show_smart_suggestions' ? 'Smart Suggestions' : key === 'show_stock_warning' ? 'Stock Warning' : 'Low Stock Warning';
        const statusText = value ? 'on' : 'off';

        handleUpdatePreferences({ [key]: value }, `${readableName} turned ${statusText}`);
    };

    const handleCurrencyChange = (e) => {
        const newCurrency = e.target.value;
        const newSymbol = getCurrencySymbol(newCurrency);
        
        setLocalCurrency(newCurrency);
        localStorage.setItem('quotation_currency', newCurrency);
        
        handleUpdatePreferences({ 
            currency: newCurrency, 
            currency_symbol: newSymbol 
        }, `Currency changed to ${newCurrency} (${newSymbol})`);
        
        window.dispatchEvent(new Event('storage'));
    };

    return (
        <div className="flex flex-col" style={{ height: 'calc(100vh - 64px)' }}>
            <UserTopbar heading="Settings" />
            <div className="flex-1 overflow-y-auto">
                <div className="w-full px-8 pt-6 pb-8 max-w-screen-2xl mx-auto space-y-6">
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-white/60 backdrop-blur-md border border-white/50 shadow-sm flex items-center justify-center">
                                    <CheckCircleRoundedIcon className="text-[#ff7b00]" style={{ fontSize: 20 }} />
                                </div>
                                <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">General Preferences</h2>
                            </div>
                        </div>
                        <div className="bg-white/60 backdrop-blur-md border border-white/50 rounded-2xl overflow-hidden shadow-lg p-5 flex flex-col lg:flex-row gap-6">
                            <div className="flex items-center justify-between w-full flex-1 p-5 bg-gradient-to-r from-white/80 to-white/40 backdrop-blur-md rounded-2xl border border-white/60 shadow-sm hover:shadow-md transition-all duration-300 group">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-[#ff7b00]/10 border border-[#ff7b00]/20 flex items-center justify-center text-[#ff7b00] group-hover:scale-105 transition-transform">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold text-gray-900 group-hover:text-[#ff7b00] transition-colors">Quotation Currency</h3>
                                        <p className="text-xs text-gray-500 mt-0.5">Select currency</p>
                                    </div>
                                </div>
                                <div className="relative">
                                    <select
                                        value={localCurrency}
                                        onChange={handleCurrencyChange}
                                        className="bg-white/90 border border-gray-200 text-gray-800 text-sm font-semibold rounded-xl focus:ring-2 focus:ring-[#ff7b00]/30 focus:border-[#ff7b00] block py-2.5 px-4 pr-10 outline-none shadow-sm cursor-pointer transition-all hover:border-gray-300"
                                    >
                                        {Object.keys(CURRENCY_SYMBOLS).map((currCode) => (
                                            <option key={currCode} value={currCode}>
                                                {currCode} ({CURRENCY_SYMBOLS[currCode]})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="flex items-center justify-between w-full flex-1 p-4 bg-white/40 rounded-xl border border-white/50">
                                <div>
                                    <h3 className="text-sm font-bold text-gray-800">Smart Suggestions</h3>
                                    <p className="text-xs text-gray-500 mt-1">Show product recommendations while building quotes.</p>
                                </div>
                                <div className="cursor-pointer transition-all" onClick={() => handleToggle('show_smart_suggestions', !localSmartSuggestions)}>
                                    {localSmartSuggestions ?
                                        <ToggleOnIcon className="text-[#ff7b00]" style={{ fontSize: 36 }} /> :
                                        <ToggleOffIcon className="text-gray-400" style={{ fontSize: 36 }} />
                                    }
                                </div>
                            </div>

                            <div className="flex items-center justify-between w-full flex-1 p-4 bg-white/40 rounded-xl border border-white/50">
                                <div>
                                    <h3 className="text-sm font-bold text-gray-800">Stock Warning</h3>
                                    <p className="text-xs text-gray-500 mt-1">Show confirmation modal for out-of-stock items.</p>
                                </div>
                                <div className="cursor-pointer transition-all" onClick={() => handleToggle('show_stock_warning', !localStockWarning)}>
                                    {localStockWarning ?
                                        <ToggleOnIcon className="text-[#ff7b00]" style={{ fontSize: 36 }} /> :
                                        <ToggleOffIcon className="text-gray-400" style={{ fontSize: 36 }} />
                                    }
                                </div>
                            </div>

                            <div className="flex items-center justify-between w-full flex-1 p-4 bg-white/40 rounded-xl border border-white/50">
                                <div>
                                    <h3 className="text-sm font-bold text-gray-800">Low Stock Warning</h3>
                                    <p className="text-xs text-gray-500 mt-1">Show low stock warning box at the bottom.</p>
                                </div>
                                <div className="cursor-pointer transition-all" onClick={() => handleToggle('show_low_stock_warning', !localLowStockWarning)}>
                                    {localLowStockWarning ?
                                        <ToggleOnIcon className="text-[#ff7b00]" style={{ fontSize: 36 }} /> :
                                        <ToggleOffIcon className="text-gray-400" style={{ fontSize: 36 }} />
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;